import { baseApi } from './base.api';
import { API_VERSION, PATH_USERS_API } from '../constants/api-routes';
import { FetchArgs } from '@reduxjs/toolkit/query';

interface Account {
  id: number;
  company_name: string;
}

interface User {
  id: number;
  role: string;
  is_active: boolean;
  email: string;
  first_name: string;
  last_name: string;
  account: number;
  managed_accounts?: {
    id: number;
    account: Account;
  }[];
}

export const usersService = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUsersMe: build.query<User, void>({
      query: () => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ME}`,
      }),
      providesTags: ['UserMe'],
    }),
    updateUsersMe: build.mutation({
      query: (body) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ME}`,
        method: 'PUT',
        body: body,
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedUser } = await queryFulfilled;
          dispatch(
            usersService.util.updateQueryData('getUsersMe', undefined, (draft) => {
              Object.assign(draft, updatedUser);
            }),
          );
        } catch {}
      },
    }),
    getUsersAll: build.query<User[], void>({
      query: (): FetchArgs => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ALL}`,
      }),
      providesTags: ['UsersAll'],
    }),
    createUser: build.mutation({
      query: (body) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.CREATE}`,
        method: 'POST',
        body: body,
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          const { data: newUser } = await queryFulfilled;

          dispatch(
            usersService.util.updateQueryData('getUsersAll', undefined, (draft: any) => {
              draft.push(newUser);
            }),
          );
        } catch {}
      },
      invalidatesTags: ['CreateUser'],
    }),
    updateUser: build.mutation({
      query: ({ userId, body }) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ROOT}/${userId}${PATH_USERS_API.UPDATE}`,
        method: 'PUT',
        body: body,
      }),
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedUser } = (await queryFulfilled) as any;
          dispatch(
            usersService.util.updateQueryData('getUsersAll', undefined, (draft: any) => {
              const currentUser = draft.find((user: any) => user.id === (updatedUser as any).id);
              Object.assign(currentUser, updatedUser);
            }),
          );
        } catch (err) {
          console.error('Error updating user:', err);
        }
      },
      invalidatesTags: ['ProjectById'],
    }),
    deleteUser: build.mutation({
      query: ({ userId }) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ROOT}/${userId}${PATH_USERS_API.DELETE}`,
        method: 'DELETE',
      }),
      invalidatesTags: ((result: any, error: any, { id }: any) => {
        if (error) {
          return [];
        }
        return [{ type: 'UsersAll', id }];
      }) as any,
    }),
  }),
});

export const {
  useGetUsersMeQuery,
  useUpdateUsersMeMutation,
  useGetUsersAllQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = usersService;
